import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PageTitle from "../../layouts/PageTitle";
import { getAuthToken } from "../../../utils/authToken";
import config from "../../../../config.json";
import "./Plans.css";

const API = import.meta.env.VITE_REACT_APP_BACKEND_URL || config.dev;

const FEATURES = [
  "packFeatureQrLuxe",
  "packFeatureUnlimitedUpdates",
  "packFeatureMultilingual",
  "packFeatureLowCost",
  "packFeaturePaperless",
  "packFeatureEasyStaff",
  "packFeatureSupport",
];

const MONEY_LOCALE = {
  en: "en-US",
  fr: "fr-FR",
  es: "es-ES",
  it: "it-IT",
  pt: "pt-PT",
  de: "de-DE",
  ar: "ar-TN",
  ru: "ru-RU",
  ja: "ja-JP",
  zh: "zh-CN",
};

function authHeaders() {
  return {
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };
}

function roundMoney(value) {
  return Math.round(Number(value) * 100) / 100;
}

function formatAmount(amount, lang) {
  const rounded = roundMoney(amount);
  const hasCents = Math.round(rounded * 100) % 100 !== 0;
  return new Intl.NumberFormat(MONEY_LOCALE[lang] || "en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(rounded);
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
}

function addMonths(value, months) {
  const date = new Date(value);
  date.setMonth(date.getMonth() + months);
  return date;
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M8 12.5l2.5 2.5L16 9.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Plans() {
  const { t, i18n } = useTranslation();
  const lang = String(i18n.language || "en").slice(0, 2);
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState("");

  const loadStatus = async () => {
    const response = await fetch(`${API}/billing/plan/status`, {
      headers: authHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || t("planLoadError"));
    setStatus(data);
    return data;
  };

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const data = await loadStatus();
        const orderId = searchParams.get("order");
        if (!orderId || ignore) return;
        const confirm = await fetch(`${API}/billing/plan/confirm`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ orderId }),
        });
        const confirmed = await confirm.json().catch(() => ({}));
        if (ignore) return;
        if (confirmed.paid) {
          toast.success(t("planPaymentSuccess"));
          await loadStatus();
        } else if (searchParams.get("payment") === "failed") {
          toast.error(t("planPaymentPending"));
        } else if (data) {
          toast.info(t("planPaymentPending"));
        }
        setSearchParams({}, { replace: true });
      } catch (error) {
        if (!ignore) toast.error(error.message || t("planLoadError"));
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
    // Confirm once when returning from iPay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (planId) => {
    setBuying(planId);
    try {
      const response = await fetch(`${API}/billing/plan/checkout`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ plan: planId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.paymentUrl) {
        throw new Error(data.message || t("planLoadError"));
      }
      window.location.assign(data.paymentUrl);
    } catch (error) {
      toast.error(error.message || t("planLoadError"));
      setBuying("");
    }
  };

  const plans = [...(status?.plans || [])].sort(
    (a, b) => Number(a.months) - Number(b.months)
  );
  const monthly = plans.find((plan) => Number(plan.months) === 1);
  const startsAt = status?.accessEndsAt ? new Date(status.accessEndsAt) : new Date();

  const daysLeft = Number(status?.daysRemaining);
  const daysTone =
    !Number.isFinite(daysLeft) || daysLeft < 7
      ? "urgent"
      : daysLeft < 15
        ? "soon"
        : "ok";

  const periodLabel = (plan) => {
    const months = Number(plan.months);
    if (months === 1) return t("packPeriodMonth");
    if (months === 6) return t("packPeriod6Months");
    if (months === 12) return t("packPeriodYear");
    return plan.name || plan.id;
  };

  return (
    <>
      <PageTitle activeMenu={t("planPageTitle")} motherMenu={t("navPlans")} />
      <section className="bo-plans">
        <div className="bo-plans__head">
          <div className="bo-plans__label">{t("packPricingSubtitle")}</div>
          <h2 className="bo-plans__title">{t("packPricingTitle")}</h2>
          <p className="bo-plans__desc">{t("packPricingDescription")}</p>
          {status && (
            <div className={`bo-plans__status bo-plans__status--${daysTone}`}>
              <p className="bo-plans__status-count">
                {status.daysRemaining === 1
                  ? t("planDayLeft")
                  : t("planDaysLeft", { count: status.daysRemaining || 0 })}
              </p>
              <p className="bo-plans__status-note">
                {status.accessEndsAt
                  ? t("planAccessEnds", { date: formatDate(status.accessEndsAt) })
                  : t("planAccessEnded")}
              </p>
            </div>
          )}
        </div>

        {loading ? (
          <p className="bo-plans__empty">{t("packLoading")}</p>
        ) : plans.length === 0 ? (
          <p className="bo-plans__empty">{t("packEmpty")}</p>
        ) : (
          <div className="bo-plans__grid">
            {plans.map((plan) => {
              const months = Math.max(1, Number(plan.months) || 1);
              const amount = roundMoney(plan.amount);
              const currency = plan.currency || "USD";
              const theoretical = monthly
                ? roundMoney(Number(monthly.amount) * months)
                : amount;
              const savings = roundMoney(theoretical - amount);
              const perMonth = amount / months;
              const best = months === 12;
              const newEnd = addMonths(startsAt, months);

              return (
                <article
                  key={plan.id}
                  className={`bo-plans__card${best ? " bo-plans__card--featured" : ""}`}
                >
                  {best && (
                    <div className="bo-plans__tags">
                      <span className="bo-plans__tag">{t("planBestValue")}</span>
                    </div>
                  )}

                  <h3 className="bo-plans__name">
                    {periodLabel(plan)}
                    {best && (
                      <span className="bo-plans__star" aria-hidden="true">
                        {" "}
                        ★
                      </span>
                    )}
                  </h3>
                  <div className="bo-plans__amount">
                    {formatAmount(amount, lang)}
                    <small> {currency}</small>
                  </div>
                  {months > 1 && (
                    <p className="bo-plans__equiv">
                      <strong>
                        {formatAmount(perMonth, lang)} {currency}
                        {t("packPerMonth")}
                      </strong>
                    </p>
                  )}
                  {savings > 0 && (
                    <p className="bo-plans__save">
                      {t("packSavings")} {formatAmount(savings, lang)} {currency}
                    </p>
                  )}
                  <ul className="bo-plans__features">
                    {FEATURES.map((featureKey) => (
                      <li key={featureKey}>
                        <CheckIcon />
                        <span>{t(featureKey)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="bo-plans__end">
                    {t("planNewEnd", { date: formatDate(newEnd) })}
                  </p>
                  <button
                    type="button"
                    className="bo-plans__buy"
                    disabled={Boolean(buying)}
                    onClick={() => buy(plan.id)}
                  >
                    {buying === plan.id ? t("planOpeningPayment") : t("planBuyThis")}
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}
